import { useEffect, useMemo, useState } from "react";
import _ from "lodash";

import scraperApi from "api";
import { Coupon } from "types/Coupons";
import { useActiveCoupons } from "routes/data-routes/billing-data/coupons/active-coupons";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";


export interface InvoiceEstimate {
  subTotal: number;
  total: number;
  amountDue: number;
  currencyCode: string;
  discounts?: Array<{ amount: number; description?: string; entityId?: string }>;
}

export interface SubscriptionEstimateResponse {
  invoiceEstimate?: InvoiceEstimate;
}

export function useSubscriptionEstimate(
  targetPlanSlug: string | undefined,
  options?: {
    newCoupon?: Coupon;
    endOfTerm?: boolean;
  }
) {
  const { activeSubscription } = useActiveSubscription();
  const { activeCoupons } = useActiveCoupons();

  const [estimate, setEstimate] = useState<SubscriptionEstimateResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Build the coupon list with type-aware replacement:
  // Chargebee only allows one coupon per discount_type (percentage / fixed_amount).
  // If the new coupon has the same type as an existing one, replace it rather than stacking.
  const newCouponCode = options?.newCoupon?.coupon_code;
  const newCouponDiscountType = options?.newCoupon?.discount_type;

  const allCouponCodes = useMemo(() => {
    const newCoupon = newCouponCode && newCouponDiscountType
      ? { coupon_code: newCouponCode, discount_type: newCouponDiscountType }
      : undefined;

    if (!activeCoupons?.length && !newCoupon) return undefined;

    let coupons = activeCoupons || [];

    if (newCoupon) {
      coupons = coupons.filter(
        coupon => coupon.discount_type !== newCoupon.discount_type
      );
      coupons = [...coupons, newCoupon as Coupon];
    }

    const codes = coupons.map(coupon => coupon.coupon_code).filter(Boolean);
    return codes.length > 0 ? codes : undefined;
  }, [activeCoupons, newCouponCode, newCouponDiscountType]);

  const fetchEstimate = useMemo(
    () => _.debounce(async (planSlug: string, couponIds: string[] | undefined, endOfTerm: boolean | undefined) => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await scraperApi.subscription.estimate(planSlug, couponIds, endOfTerm);
        setEstimate(result);
      } catch (err: any) {
        setError(err?.message || "Failed to get estimate");
        setEstimate(null);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    if (!targetPlanSlug || !activeSubscription) {
      setEstimate(null);
      return;
    }

    fetchEstimate(targetPlanSlug, allCouponCodes, options?.endOfTerm);

    return () => {
      fetchEstimate.cancel();
    };
  }, [targetPlanSlug, allCouponCodes, options?.endOfTerm, activeSubscription, fetchEstimate]);

  return { estimate, isLoading, error };
}
